import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Crown, BadgeCheck, ArrowRight, Sparkles, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

/**
 * Subscription status card for the seller dashboard.
 * - Free tier: shows upgrade CTA + live "X of 100 founding spots remaining" counter
 * - Premium/Founding: shows tier, badge, renews-on date, and cancel option
 */
export default function SubscriptionCard({ business }) {
  const [founding, setFounding] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [statusOverride, setStatusOverride] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/subscription/founding-status`);
        if (!cancelled) setFounding(res.data);
      } catch (e) {
        // non-fatal — banner just won't show
        if (process.env.NODE_ENV !== "production") console.debug("[subscription] founding-status fetch failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tier = statusOverride?.tier || business?.subscription_tier || "free";
  const isPremium = tier === "premium" || tier === "founding";
  const isFounding = tier === "founding" || business?.is_founding_member;
  const subStatus = statusOverride?.status || business?.subscription_status;
  const isCanceling = subStatus === "canceling" || business?.subscription_cancel_at_period_end;
  const renewDate = business?.subscription_current_period_end
    ? new Date(business.subscription_current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  const handleCancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the billing period? You'll keep Premium features until then.")) return;
    setCanceling(true);
    try {
      const res = await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      setStatusOverride({ status: "canceling" });
      toast.success("Subscription set to cancel at period end");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel subscription");
    } finally {
      setCanceling(false);
    }
  };

  // ---- Free tier card: upsell ----
  if (!isPremium) {
    return (
      <div
        data-testid="subscription-card-free"
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-tbc-navy to-[#0E2240] text-white p-6 mb-6 shadow-lg"
      >
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-tbc-gold/10 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-tbc-gold/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-tbc-gold" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs tracking-[0.2em] uppercase text-tbc-gold font-medium">Free Plan</p>
                {founding?.available && founding.remaining > 0 && (
                  <span className="text-[10px] bg-tbc-gold text-tbc-navy px-2 py-0.5 rounded-full font-bold">
                    {founding.remaining} FOUNDING SPOTS LEFT
                  </span>
                )}
              </div>
              <h3 className="font-heading text-xl text-white leading-tight">Unlock Premium features</h3>
              <p className="text-white/70 text-sm mt-1">
                Featured placement, gold badge, unlimited products, analytics & more.
                {founding?.available && founding.remaining > 0 && (
                  <> Lock in <strong className="text-tbc-gold">$9.99/mo</strong> for life as a Founding Member.</>
                )}
              </p>
            </div>
          </div>
          <Link to="/pricing" data-testid="subscription-upgrade-cta">
            <Button className="rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold whitespace-nowrap">
              Upgrade <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // ---- Premium / Founding card: status ----
  return (
    <div
      data-testid="subscription-card-active"
      className={`rounded-2xl p-6 mb-6 shadow-sm border ${isFounding ? "bg-gradient-to-br from-tbc-navy to-[#0E2240] text-white border-tbc-gold/40" : "bg-white border-tbc-navy/10 text-tbc-navy"}`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isFounding ? "bg-tbc-gold" : "bg-tbc-gold/15"}`}>
            {isFounding ? <Crown className="w-5 h-5 text-tbc-navy" /> : <BadgeCheck className="w-5 h-5 text-tbc-gold" />}
          </div>
          <div>
            <p className={`text-xs tracking-[0.2em] uppercase font-medium ${isFounding ? "text-tbc-gold" : "text-tbc-navy/60"}`}>
              {isFounding ? `Founding Member #${business?.founding_member_number || "—"}` : "Premium Member"}
            </p>
            <h3 className={`font-heading text-xl leading-tight ${isFounding ? "text-white" : "text-tbc-navy"}`}>
              {isFounding ? "Hall of Fame · Locked for life" : "Premium plan active"}
            </h3>
            <p className={`text-sm mt-1 ${isFounding ? "text-white/70" : "text-tbc-navy/65"}`}>
              {isCanceling ? (
                <>Cancels on <strong>{renewDate || "period end"}</strong>. You keep features until then.</>
              ) : (
                <>Renews {renewDate ? <>on <strong>{renewDate}</strong></> : "automatically"} · {business?.subscription_period || "—"}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isCanceling && (
            <Button
              variant="ghost"
              onClick={handleCancel}
              disabled={canceling}
              data-testid="subscription-cancel"
              className={`rounded-full ${isFounding ? "text-white/70 hover:text-white hover:bg-white/10" : "text-tbc-navy/60 hover:text-tbc-navy"}`}
            >
              {canceling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Cancel subscription
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
